import { Schemas } from '../types/api-types';
import apiClient from '../clients/apiClient';

export type PlAccountInfoResponse = Schemas['PlAccountInfoResponse'];
export type PlDocumentResponse = Schemas['PlDocumentsResponse'];

export const fetchDynamicFields = async (plAccountId: number): Promise<PlAccountInfoResponse[]> => {
    const response = await apiClient.get(`/loan-application/account_info/${plAccountId}`);
    return response.data.data || [];
};

export const fetchDocumentFields = async (plAccountId: number): Promise<PlDocumentResponse[]> => {
    const response = await apiClient.get(`/loan-application/documents/${plAccountId}`);
    return response.data.data || [];
};

export const updateInfoField = async (infoId: number, value: string) => {
    // Use correct endpoint for saving dynamic fields
    return await apiClient.put(`/loan-application/pl_account_info/${infoId}`, { information: value });
};

export const uploadDocumentFile = async (docId: number, file: File): Promise<PlDocumentResponse> => {
    const formData = new FormData();
    formData.append('file', file); // must match backend param name
    // Use new endpoint: /loan-application/documents/upload/{pl_document_id}
    const response = await apiClient.post(`/loan-application/documents/upload/${docId}`, formData);
    return response.data.data;
};

export type PlGuarantorResponse = {
    max_guarantors: number;
    required_guarantors: number;
    existing_guarantors: { 
        id: number; 
        guarantor_type_id?: number | null;
        ci_customer_id?: number | null;
        customer_number: string | null; 
        name: string | null; 
        name_en?: string | null;
        name_si?: string | null;
        name_ta?: string | null;
        nic: string | null;
    }[];
}

export const fetchLoanGuaranters = async (plAccountId: number): Promise<PlGuarantorResponse> => {
    const response = await apiClient.get(`/loan-application/guaranters/${plAccountId}`);
    return response.data.data;
};

export type PlGuarantorListResponse = {
    id?: number | null;
    customer_number: string | null;
    nic?: string | null;
    name?: string | null;
    name_en?: string | null;
    name_si?: string | null;
    name_ta?: string | null;
}

export const fetchGuarantors = async (plAccountId: number, value: any): Promise<PlGuarantorListResponse[]> => {
    const response = await apiClient.get(`/loan-application/guaranters-list/${plAccountId}/${value}`);
    return response.data.data;
};

export const saveGuarantors = async (plAccountId: number, payload: any) => {
    try {
        const response =  await apiClient.post(`/loan-application/guaranters/${plAccountId}`, payload);
        return response.data.data;
    } catch (error: any) {
        if (error.response) {
            throw new Error(error.response.data.detail || "Failed to save guarantors");
        }
        throw new Error("Network error, please contact support team");
    }
}

export const fetchGuarantorsForPrint = async (plAccountId: number): Promise<Array<{index: number, guarantor_type: string, customer_number: string}>> => {
    const response = await apiClient.get(`/loan-application/guarantors-for-print/${plAccountId}`);
    return response.data.data;
}

export const getDocumentDownloadUrl = async (documentId: number) => {
    return await apiClient.get(`/loan-application/documents/download/${documentId}`);
};

export type GuarantorInsuranceItem = {
    customer_number?: string | null;
    name_en?: string | null;
    name_si?: string | null;
    name_ta?: string | null;
    percentage: number;
};

export type GuarantorInsuranceResponse = {
    id: number;
    pl_account_id: number;
    insurance_info: GuarantorInsuranceItem[];
    insurance_instalment?: number;
};

export const fetchGuarantorInsurance = async (plAccountId: number): Promise<GuarantorInsuranceResponse | null> => {
    const response = await apiClient.get(`/loan-application/insurance-info/${plAccountId}`);
    return response.data.data;
};

export const saveGuarantorInsurance = async (plAccountId: number, payload: { insurance_info: GuarantorInsuranceItem[]; insurance_instalment?: number }): Promise<GuarantorInsuranceResponse> => {
    try {
        const response = await apiClient.post(`/loan-application/insurance-info/${plAccountId}`, payload);
        return response.data.data;
    } catch (error: any) {
        if (error.response) {
            throw new Error(error.response.data.detail || "Failed to save guarantor insurance");
        }
        throw new Error("Network error, please contact support team");
    }
};
